import type { DivergenciaDeSaldo, StatusDivergenciaSaldo } from '../../api/divergencias-saldo.js';
import type { TomStatus } from '../../componentes/ChipStatus.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';
import { formatarQuantidade } from '../movimentacoes/quantidade.js';

/**
 * Como a tela lê uma divergência de saldo (r1-sync): o status, a diferença e
 * as quantidades comparadas, lado a lado.
 */

const ROTULO_DO_STATUS: Readonly<Record<StatusDivergenciaSaldo, string>> = {
  Pendente: 'Pendente',
  // "Atualizada" sozinho não diz por quem: o saldo passou a ser o do ERP.
  Atualizada: 'Atualizada pelo ERP',
  Mantida: 'Mantida',
};

const TOM_DO_STATUS: Readonly<Record<StatusDivergenciaSaldo, TomStatus>> = {
  Pendente: 'divergencia',
  Atualizada: 'concluida',
  Mantida: 'desativado',
};

export function rotuloDoStatus(status: StatusDivergenciaSaldo): string {
  return ROTULO_DO_STATUS[status];
}

export function tomDoStatus(status: StatusDivergenciaSaldo): TomStatus {
  return TOM_DO_STATUS[status];
}

export const STATUS_TODOS = 'todas';

/** A tela abre nas pendentes: é a pergunta que traz a pessoa até aqui. */
export const STATUS_PADRAO: StatusDivergenciaSaldo = 'Pendente';

const STATUS = Object.keys(ROTULO_DO_STATUS) as StatusDivergenciaSaldo[];

export const OPCOES_DE_STATUS: readonly OpcaoFiltro[] = [
  { valor: 'Pendente', rotulo: 'Pendentes' },
  { valor: 'Atualizada', rotulo: 'Atualizadas pelo ERP' },
  { valor: 'Mantida', rotulo: 'Mantidas' },
  { valor: STATUS_TODOS, rotulo: 'Situação · todas' },
];

/** Valor do seletor → recorte do servidor. O que não for status não recorta. */
export function statusDoFiltro(valor: string): StatusDivergenciaSaldo | null {
  return (STATUS as readonly string[]).includes(valor) ? (valor as StatusDivergenciaSaldo) : null;
}

const POSITIVO = /^\d+(?:\.\d+)?$/;
const ZERO = /^0+(?:\.0+)?$/;

/**
 * A diferença (ERP menos local) com o sinal sempre escrito: "+2" e "2" lidos
 * de relance numa coluna de números são a mesma coisa, e não são.
 */
export function formatarDiferenca(texto: string): string {
  const formatada = formatarQuantidade(texto);
  const limpo = texto.trim();

  if (!POSITIVO.test(limpo) || ZERO.test(limpo)) {
    return formatada;
  }

  return `+${formatada}`;
}

/**
 * Rótulos das quantidades do saldo, na ordem em que a tela as lista. As
 * chaves são as de `QUANTIDADES_DO_SALDO` no middleware
 * (`src/sincronizacao/divergencias-saldo/valores-do-saldo.ts`).
 */
const ROTULO_DA_QUANTIDADE: Readonly<Record<string, string>> = {
  quantidadeDisponivel: 'Disponível',
  quantidadeEstoque: 'Em estoque',
  quantidadeReservada: 'Reservada',
  quantidadeReservaExclusiva: 'Reserva exclusiva',
  quantidadeBloqueada: 'Bloqueada',
  quantidadeEmOrdens: 'Em ordens',
  quantidadeEmTransito: 'Em trânsito',
};

const ORDEM = Object.keys(ROTULO_DA_QUANTIDADE);

const DISPONIVEL = 'quantidadeDisponivel';

/** Texto que o leitor vê no lugar de `null`: o ERP não manda esta quantidade. */
export const NAO_INFORMADO = 'não informado';

export type LinhaDaComparacao = {
  readonly chave: string;
  readonly rotulo: string;
  readonly local: string;
  readonly erp: string;
  readonly difere: boolean;
};

function exibir(valor: string | null | undefined): string {
  if (valor === undefined) {
    return '—';
  }
  return valor === null ? NAO_INFORMADO : formatarQuantidade(valor);
}

function posicao(chave: string): number {
  const indice = ORDEM.indexOf(chave);
  return indice === -1 ? ORDEM.length : indice;
}

/**
 * As quantidades comparadas, local ao lado do ERP.
 *
 * Só entram as chaves que a sincronização comparou (presentes em algum dos
 * dois lados): chave ausente quer dizer "não comparada", e o `Atualizar` não a
 * aplica — listar uma linha para ela sugeriria que ela muda. A disponível
 * entra sempre, com os números das colunas próprias: é a que o `Atualizar`
 * aplica a partir de `quantidadeDisponivelErp`, mesmo quando os valores
 * gravados saíram do contrato e vieram `null`.
 *
 * Chave fora do catálogo aparece pelo próprio nome — inventar rótulo plausível
 * para uma quantidade que ninguém previu é como a tela passa a afirmar o que
 * não sabe.
 */
export function linhasDaComparacao(divergencia: DivergenciaDeSaldo): readonly LinhaDaComparacao[] {
  const local = divergencia.valoresLocal ?? {};
  const erp = divergencia.valoresErp ?? {};
  const chaves = new Set([DISPONIVEL, ...Object.keys(local), ...Object.keys(erp)]);

  return [...chaves]
    .sort((a, b) => posicao(a) - posicao(b))
    .map((chave) => {
      const valorLocal =
        chave === DISPONIVEL ? divergencia.quantidadeDisponivelLocal : local[chave];
      const valorErp = chave === DISPONIVEL ? divergencia.quantidadeDisponivelErp : erp[chave];
      const textoLocal = exibir(valorLocal);
      const textoErp = exibir(valorErp);

      return {
        chave,
        rotulo: ROTULO_DA_QUANTIDADE[chave] ?? chave,
        local: textoLocal,
        erp: textoErp,
        difere: textoLocal !== textoErp,
      };
    });
}
